import React from 'react';
import { Box, Typography, Divider, Chip, useTheme } from '@mui/material';
import { Organisation } from '../../types/organization';
import { Building2, Globe, MapPin, Phone, Mail } from 'lucide-react';

interface OrganizationInfoProps {
  organization: Organisation | null;
}

const InfoItem = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
      <Box
        sx={{
          mr: 2,
          color: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
          borderRadius: 1,
          backgroundColor: `${theme.palette.primary.main}15`,
        }}
      >
        <Icon size={16} />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2">{value || 'Not provided'}</Typography>
      </Box>
    </Box>
  );
};

const OrganizationInfo: React.FC<OrganizationInfoProps> = ({ organization }) => {
  if (!organization) {
    return null;
  }

  return (
    <Box sx={{ mb: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {organization.company.name || 'Not provided'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {organization.company.description || 'Not provided'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={organization.isBookingEnabled ? 'Booking Enabled' : 'Booking Disabled'}
            size="small"
            color={organization.isBookingEnabled ? 'success' : 'default'}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-evenly',
          flexGrow: 1,
          gap: 10,
          mt: 4,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Building2 size={16} />
            Company Contact
          </Typography>

          <InfoItem
            icon={Globe}
            label="Website"
            value={organization.company.website || 'Not provided'}
          />
          <InfoItem
            icon={MapPin}
            label="Address"
            value={
              organization.company.address &&
              organization.company.city &&
              organization.company.state &&
              organization.company.postcode
                ? `${organization.company.address}, ${organization.company.city}, ${organization.company.state} ${organization.company.postcode}`
                : 'Not provided'
            }
          />
          <InfoItem icon={Phone} label="Phone" value={organization.company.phone} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Mail size={16} />
            Client Contact
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {`${organization.client.firstName} ${organization.client.lastName}`}
            </Typography>
            <Chip
              label={`Role: ${organization.client.role === 'client_admin' ? 'Admin' : 'User'}`}
              size="small"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <InfoItem icon={Mail} label="Email" value={organization.client.email} />
          <InfoItem icon={Phone} label="Phone" value={organization.client.phone} />
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationInfo;
